import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import PaginationButtons from 'src/components/ui/PaginationButtons/PaginationButtons';
import { useWorkflowRunsQuery, WorkflowRun, WorkflowRunStats, WorkflowRunStatus } from 'src/services/workflows';
import { formatDate, formatTime } from 'src/utils/general';
import WorkflowSteps from '../WorkflowSteps/WorkflowSteps';
import styles from './WorkflowRuns.module.scss';

const PAGE_SIZE = 20;

const STATUS_BADGE: Record<WorkflowRunStatus, BadgeType> = {
  success: BadgeType.Successful,
  fail: BadgeType.Error,
  skipped: BadgeType.Warning,
};

export const runStatusBadgeType = (status: WorkflowRun['status']): BadgeType =>
  status ? STATUS_BADGE[status] : BadgeType.Neutral;

const toRelativePath = (url: string) => {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

interface WorkflowRunsProps {
  workflowId: number;
  stats: WorkflowRunStats;
}

const WorkflowRuns: React.FC<WorkflowRunsProps> = ({ workflowId, stats }) => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<WorkflowRunStatus | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [openRun, setOpenRun] = useState<number | null>(null);
  const { data, isLoading } = useWorkflowRunsQuery(workflowId, { page, size: PAGE_SIZE, run_status: statusFilter });

  const runs = data?.items ?? [];
  const pages = data?.pages ?? 1;

  const selectFilter = (status?: WorkflowRunStatus) => {
    setStatusFilter(status);
    setPage(1);
  };

  const filters: { key: string; label: string; status?: WorkflowRunStatus; count: number }[] = [
    { key: 'all', label: t('workflows.all_runs'), count: stats.lifetime.total },
    { key: 'success', label: t('workflows.run_status.success'), status: 'success', count: stats.lifetime.success },
    { key: 'skipped', label: t('workflows.run_status.skipped'), status: 'skipped', count: stats.lifetime.skipped },
    { key: 'fail', label: t('workflows.run_status.fail'), status: 'fail', count: stats.lifetime.failed },
  ];

  const runDetails = (run: WorkflowRun) => {
    const reason = run.reason ? t(`workflows.run_reason.${run.reason}`, { defaultValue: run.reason }) : '';
    if (run.message && reason && run.reason !== 'error') return `${reason} · ${run.message}`;
    return run.message || reason || '—';
  };

  return (
    <div className="flex flex-col gap-y-20">
      <div className={styles.tiles}>
        <div className={styles.tile}>
          <span className="typo-text-12 text-faded">{t('workflows.runs_total')}</span>
          <span className={styles.tileValue}>{stats.total}</span>
        </div>
        <div className={styles.tile}>
          <span className="typo-text-12 text-faded">{t('workflows.runs_success')}</span>
          <span className={styles.tileValue} style={{ color: 'var(--status-success-full)' }}>
            {stats.success}
          </span>
          <span className="typo-text-12 text-faded">
            {stats.skipped} {t('workflows.runs_skipped').toLowerCase()}
          </span>
        </div>
        <div className={styles.tile}>
          <span className="typo-text-12 text-faded">{t('workflows.runs_failed')}</span>
          <span className={styles.tileValue} style={{ color: 'var(--status-error-full)' }}>
            {stats.failed}
          </span>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <div className={`${styles.filters} p-12 border-subtle-bottom`}>
          {filters.map(filter => (
            <button
              key={filter.key}
              type="button"
              className={`${styles.filter} ${statusFilter === filter.status ? styles.filterActive : ''}`}
              onClick={() => selectFilter(filter.status)}>
              {filter.label} {filter.count}
            </button>
          ))}
        </div>
        {!isLoading && runs.length === 0 ? (
          <div className={styles.empty}>{t('workflows.no_runs')}</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('workflows.executed_at')}</th>
                <th>{t('workflows.record')}</th>
                <th>{t('workflows.result')}</th>
                <th>{t('workflows.details')}</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(run => (
                <React.Fragment key={run.id}>
                  <tr>
                    <td>
                      {run.executed_at ? (
                        <div className="flex flex-col gap-y-4">
                          <span>{formatDate(run.executed_at)}</span>
                          <span className="typo-text-12 text-faded">{formatTime(run.executed_at)}</span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {run.deep_link ? (
                        <Link className={styles.recordLink} to={toRelativePath(run.deep_link)}>
                          {run.record_name || `#${run.record_id}`}
                        </Link>
                      ) : (
                        <span>{run.record_name || `#${run.record_id}`}</span>
                      )}
                    </td>
                    <td>
                      <Badge
                        expanded
                        showDot
                        dotFilled
                        type={runStatusBadgeType(run.status)}
                        label={run.status ? t(`workflows.run_status.${run.status}`) : '—'}
                      />
                    </td>
                    <td className="typo-text-12">
                      <div className="flex items-center gap-x-8">
                        <span>{runDetails(run)}</span>
                        {run.steps?.length > 0 && (
                          <button
                            type="button"
                            className={styles.stepsToggle}
                            aria-label={openRun === run.id ? t('workflows.hide_steps') : t('workflows.show_steps')}
                            onClick={() => setOpenRun(openRun === run.id ? null : run.id)}>
                            {openRun === run.id ? t('workflows.hide_steps') : t('workflows.show_steps')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {openRun === run.id && run.steps?.length > 0 && (
                    <tr className={styles.stepsRow}>
                      <td colSpan={4}>
                        <span className="typo-text-12--bold text-faded block mb-8">{t('workflows.steps')}</span>
                        <WorkflowSteps steps={run.steps} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        {pages > 1 && (
          <div className={styles.footer}>
            <span>
              {page} / {pages}
            </span>
            <PaginationButtons
              onClick={direction => setPage(current => (direction === 'next' ? current + 1 : current - 1))}
              disabledPrevious={page <= 1}
              disabledNext={page >= pages}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowRuns;
